import * as React from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Link, Avatar, Chip } from '@mui/material';
// import { TitleButton } from './TitleButton'; // --- 移除 TitleButton 导入 ---
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import config from '../../config';
import SubtitleApi from '../../api/SubtitleApi'; // --- 新增导入：SubtitleApi ---
import { context as globalContext } from '../../context'; // --- 新增导入：全局上下文 ---
import { context } from '../context'; // --- 新增导入：Home 上下文 ---
import { SubtitleDialog } from '../subtitle/SubtitleDialog'; // --- 新增导入：SubtitleDialog ---


// 直播形式、内容及其对应颜色的映射关系 ---
const liveFormatMap = {
    1: '3D',
    2: '2D',
    3: '电台',
    4: '联动',
    5: '双播',
    6: '其它',
};

const liveContentMap = {
    1: '才艺',
    2: '杂谈',
    3: '游戏',
    4: '综艺',
    5: '活动',
    6: '观影',
    7: '特殊',
    8: '投稿',
    9: '歌回',
    10: '其它',
};

// Premium color palette - vibrant but refined
const liveContentColorMap = {
    1: '#FF8B93', // 才艺 - theme coral
    2: '#74B9FF', // 杂谈 - sky blue
    3: '#55EFC4', // 游戏 - mint green
    4: '#FDCB6E', // 综艺 - warm yellow
    5: '#A29BFE', // 活动 - soft purple
    6: '#81ECEC', // 观影 - turquoise
    7: '#FD79A8', // 特殊 - pink
    8: '#00CEC9', // 投稿 - teal
    9: '#FF7675', // 歌回 - coral red
    10: '#B2BEC3',// 其它 - muted gray
};

// 高亮关键词的辅助函数
const highlightKeyword = (text, keyword) => {
    if (!keyword || !text) return text;
    
    const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
        part.toLowerCase() === keyword.toLowerCase() 
            ? <span key={index} style={{ color: '#FF8B93', fontWeight: 600 }}>{part}</span>
            : part
    );
};

export const ClipCard = ({ clip, clipGroup }) => {
    const [date] = clip.datetime.split(' ');
    const liveFormatText = liveFormatMap[clip.live_format] || '其它';
    const liveContentText = liveContentMap[clip.live_content] || '其它';
    const liveContentColor = liveContentColorMap[clip.live_content] || '#B2BEC3';

    // --- 新增：SubtitleDialog 相关的状态 ---
    const [dialogStatus, setDialogStatus] = React.useState(false);
    const [dialogSubtitles, setDialogSubtitles] = React.useState([]);
    const { setLoading, onMessage } = React.useContext(globalContext);
    const { searchWord, searchType } = React.useContext(context); // 获取搜索词和搜索类型

    // 判断是否为 AI 总结搜索结果
    const isSummarySearch = searchType === 'summary' && searchWord;

    // 检查是否有任一分P视频有 AI 总结
    const hasAnySummary = React.useMemo(() => {
        if (!clipGroup || clipGroup.length === 0) return clip.hasSummary;
        return clipGroup.some(c => c.hasSummary);
    }, [clipGroup, clip.hasSummary]);

    // --- 收集同一 BV 号下所有分P的预览 ---
    const allPreviews = React.useMemo(() => {
        if (!searchWord || !clipGroup || clipGroup.length === 0) return [];
        
        const previews = [];
        clipGroup.forEach(c => {
            if (c.searchPreviews && c.searchPreviews.length > 0) {
                c.searchPreviews.forEach(preview => {
                    previews.push({
                        ...preview,
                        clipId: c.id,
                        part: c.part || 1
                    });
                });
            }
        });
        // 按 part 和 lineId 排序
        previews.sort((a, b) => {
            if (a.part !== b.part) return a.part - b.part;
            return a.lineId - b.lineId;
        });
        return previews.slice(0, 4); // 最多显示4条
    }, [clipGroup, searchWord]);

    const totalMatchCount = React.useMemo(() => {
        if (!clipGroup) return 0;
        return clipGroup.reduce((sum, c) => {
            return sum + (c.searchPreviews ? c.searchPreviews.length : 0);
        }, 0);
    }, [clipGroup]);

    // --- 提取 TitleButton 的 onClick 逻辑 ---
    const handleCardClick = async () => {
        setLoading(true);
        try {
            // 点击时，默认获取第一个分P的字幕
            const json = await SubtitleApi.findByClipId(clip.id, searchWord);
            setDialogSubtitles(json || []);
            setDialogStatus(true);
        } catch (ex) {
            console.log(ex);
            if (ex) {
                onMessage({
                    type: 'error',
                    content: `${ex.code}:${ex.message}`
                });
            } else {
                onMessage({
                    type: 'error',
                    content: '内部错误'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card 
            sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.04)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'hidden',
                '&:hover': {
                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.08)',
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(255, 139, 147, 0.2)',
                },
            }}
        >
            <Box 
                sx={{ position: 'relative', cursor: 'pointer' }} 
                onClick={handleCardClick}
            >
                <CardMedia
                    component="img"
                    image={`//${clip.cover}`}
                    alt={clip.title}
                    sx={{ 
                        aspectRatio: '16/9', 
                        objectFit: 'cover',
                    }}
                />
                {/* Overlay gradient */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
                    pointerEvents: 'none',
                }} />
                
                {/* Top badge */}
                {(clip.type === 1 || clip.type === 4) && (
                    <Box sx={{ 
                        position: 'absolute', 
                        top: 10, 
                        left: 10, 
                        bgcolor: 'rgba(255, 255, 255, 0.95)', 
                        color: 'text.primary', 
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '6px', 
                        fontSize: '0.6875rem', 
                        fontWeight: 600,
                        display: 'flex', 
                        alignItems: 'center',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}>
                        {clip.type === 4 && ( 
                            <Box 
                                component="span" 
                                sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: '#FF8B93',
                                    mr: 0.75,
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.5 },
                                    },
                                }} 
                            />
                        )}
                        {clip.type === 1 ? liveFormatText : '直播中'}
                    </Box>
                )}

                {/* AI Summary indicator - 右上角 */}
                {hasAnySummary && (
                    <>
                        <svg width="0" height="0" style={{ position: 'absolute' }}>
                            <linearGradient id={`ai-gradient-${clip.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#FFFFFF">
                                    <animate
                                        attributeName="stop-color"
                                        values="#FFFFFF;#FFEFF4;#FFFFFF"
                                        dur="3.6s"
                                        repeatCount="indefinite"
                                    />
                                </stop>
                                <stop offset="42%" stopColor="#FFD6E2">
                                    <animate
                                        attributeName="stop-color"
                                        values="#FFD6E2;#FFBFD1;#FFD6E2"
                                        dur="3.6s"
                                        repeatCount="indefinite"
                                    />
                                </stop>
                                <stop offset="100%" stopColor="#FF8B93">
                                    <animate
                                        attributeName="stop-color"
                                        values="#FF8B93;#FF7298;#FF8B93"
                                        dur="3.6s"
                                        repeatCount="indefinite"
                                    />
                                </stop>
                                <animateTransform
                                    attributeName="gradientTransform"
                                    type="rotate"
                                    values="0 0.5 0.5;18 0.5 0.5;0 0.5 0.5"
                                    dur="5.5s"
                                    repeatCount="indefinite"
                                />
                            </linearGradient>
                        </svg>
                        <AutoAwesomeIcon 
                            sx={{ 
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                fontSize: '1.4rem',
                                fill: `url(#ai-gradient-${clip.id})`,
                                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))',
                                animation: 'aiSummaryIconGlow 3.6s ease-in-out infinite',
                                '@keyframes aiSummaryIconGlow': {
                                    '0%, 100%': {
                                        opacity: 0.96,
                                        transform: 'scale(1)',
                                    },
                                    '50%': {
                                        opacity: 1,
                                        transform: 'scale(1.04)',
                                    },
                                },
                            }} 
                        />
                    </>
                )}
                
                {/* Bottom info */}
                <Box sx={{ 
                    position: 'absolute', 
                    bottom: 10, 
                    left: 10, 
                    right: 10,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'rgba(255,255,255,0.95)', 
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                    >
                        {date}
                    </Typography>
                    <Box 
                        sx={{ 
                            bgcolor: liveContentColor, 
                            color: 'white', 
                            px: 1,
                            py: 0.25,
                            borderRadius: '4px', 
                            fontSize: '0.625rem', 
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                        }}
                    >
                        {liveContentText}
                    </Box>
                </Box>
            </Box>
            
            <CardContent 
                sx={{ 
                    flexGrow: 1, 
                    p: 2,
                    pb: '16px !important',
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.01)',
                    },
                }} 
                onClick={handleCardClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25 }}>
                    <Link 
                        href={`${config.url.author}/${clip.author.uid}`} 
                        underline='none' 
                        rel='noopener' 
                        target='_blank' 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Avatar 
                            sx={{ 
                                width: 24, 
                                height: 24, 
                                mr: 1,
                                border: '1.5px solid',
                                borderColor: 'rgba(0, 0, 0, 0.06)',
                                boxShadow: 'none',
                            }} 
                            src={`//${clip.author.avatar}@60w_60h.webp`} 
                            alt={clip.author.name} 
                        />
                    </Link>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                        }}
                    >
                        {clip.author.name}
                    </Typography>
                </Box>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        fontWeight: 600,
                        lineHeight: 1.4,
                        color: 'text.primary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.875rem',
                    }}
                >
                    {clip.mainTitle || clip.title}
                </Typography>
            </CardContent>

            {/* --- 搜索预览区域 --- */}
            {searchWord && !isSummarySearch && (
                <Box sx={{ 
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(0, 0, 0, 0.015)',
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    minHeight: 80,
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        bgcolor: 'rgba(255, 139, 147, 0.03)',
                    },
                }} onClick={handleCardClick}>
                    {allPreviews.length > 0 ? (
                        <>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 0.75
                            }}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'text.disabled',
                                        fontWeight: 500,
                                        fontSize: '0.625rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    匹配 {totalMatchCount} 条
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {allPreviews.map((preview, idx) => (
                                    <Typography 
                                        key={`${preview.clipId}-${preview.lineId}-${idx}`} 
                                        variant="caption" 
                                        sx={{ 
                                            color: 'text.secondary',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.5,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block'
                                        }}
                                    >
                                        <Box 
                                            component="span" 
                                            sx={{ 
                                                color: 'text.disabled', 
                                                fontWeight: 600,
                                                fontSize: '0.625rem',
                                                mr: 0.75,
                                            }}
                                        >
                                            P{preview.part}
                                        </Box>
                                        {highlightKeyword(preview.content, searchWord)}
                                    </Typography>
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            minHeight: 50,
                        }}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.disabled',
                                    fontSize: '0.75rem',
                                }}
                            >
                                加载中...
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* --- AI 总结搜索预览区域 --- */}
            {isSummarySearch && (clip.summaryKeywords || clip.overallSummary) && (
                <Box sx={{ 
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(255, 139, 147, 0.02)',
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        bgcolor: 'rgba(255, 139, 147, 0.06)',
                    },
                }} onClick={handleCardClick}>
                    {/* AI 总结标识 */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        mb: 1,
                    }}>
                        <AutoAwesomeIcon sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.625rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            AI 总结匹配
                        </Typography>
                    </Box>
                    
                    {/* 匹配的关键词 */}
                    {clip.summaryKeywords && clip.summaryKeywords.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {clip.summaryKeywords.slice(0, 5).map((keyword, idx) => (
                                <Chip
                                    key={idx}
                                    label={keyword}
                                    size="small"
                                    sx={{
                                        height: '20px',
                                        fontSize: '0.625rem',
                                        fontWeight: 500,
                                        bgcolor: keyword.toLowerCase().includes(searchWord.toLowerCase()) 
                                            ? 'rgba(255, 139, 147, 0.15)'
                                            : 'rgba(0, 0, 0, 0.05)',
                                        color: keyword.toLowerCase().includes(searchWord.toLowerCase())
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        border: keyword.toLowerCase().includes(searchWord.toLowerCase())
                                            ? '1px solid'
                                            : 'none',
                                        borderColor: 'primary.light',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    
                    {/* 内容概述预览 */}
                    {clip.overallSummary && (
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {highlightKeyword(clip.overallSummary, searchWord)}
                        </Typography>
                    )}
                </Box>
            )}

            {/* --- 在 ClipCard 内部渲染 SubtitleDialog --- */}
            <SubtitleDialog 
                status={dialogStatus} 
                setStatus={setDialogStatus} 
                clip={clip}
                clipGroup={clipGroup} 
                initialSubtitles={dialogSubtitles} 
                searchWord={searchWord}
                initialTab={isSummarySearch ? 2 : 0}
            />
        </Card>
    );
};